import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { putRequest } from "../../../helpers/api.helper";
import { ProfileCard } from "../../../types/profile";

import { BureauDashboardData, ProfileSectionConfig } from "../dashboard.types";
import { getProfileIdentifier, parseNumericValue } from "../dashboard.utils";

type PaymentStatus = "free" | "paid" | "redzone";

type UsePaymentStatusParams = {
  profileSections: ProfileSectionConfig[];
  setProfileSections: React.Dispatch<
    React.SetStateAction<ProfileSectionConfig[]>
  >;
  fetchDashboardData: (options?: {
    manual?: boolean;
  }) => Promise<BureauDashboardData | null>;
};

type PaymentUpdatingState = {
  id: string | number;
  status: PaymentStatus;
} | null;

const PAYMENT_STATUS_SECTION_MAP: Record<PaymentStatus, string> = {
  free: "free-profiles",
  paid: "paid-profiles",
  redzone: "redzone-profiles",
};

const usePaymentStatus = ({
  setProfileSections,
  fetchDashboardData,
}: UsePaymentStatusParams) => {
  const [paymentUpdating, setPaymentUpdating] =
    useState<PaymentUpdatingState>(null);

  const adjustSectionCount = useCallback(
    (section: ProfileSectionConfig, delta: number): ProfileSectionConfig => {
      const currentCount = parseNumericValue(section.totalCount);

      if (currentCount === null) {
        return {
          ...section,
          totalCount: Math.max(0, section.profiles.length + delta),
        };
      }

      return {
        ...section,
        totalCount: Math.max(0, Math.round(currentCount + delta)),
      };
    },
    [],
  );

  const moveProfileBetweenSections = useCallback(
    (
      profileKey: string | number,
      fromSectionKey: string,
      toSectionKey: string,
      nextStatus: PaymentStatus,
    ) => {
      setProfileSections((previousSections) => {
        let movedProfile: ProfileCard | null = null;

        const sectionsAfterRemoval = previousSections.map((section) => {
          if (section.key !== fromSectionKey) {
            return section;
          }

          const remainingProfiles = section.profiles.filter((profile) => {
            const isTargetProfile =
              getProfileIdentifier(profile) === profileKey;

            if (isTargetProfile) {
              movedProfile = {
                ...profile,
                paymentStatus: nextStatus,
              };

              return false;
            }

            return true;
          });

          if (!movedProfile) {
            return section;
          }

          return adjustSectionCount(
            {
              ...section,
              profiles: remainingProfiles,
            },
            -1,
          );
        });

        if (!movedProfile) {
          return previousSections;
        }

        return sectionsAfterRemoval.map((section) => {
          if (section.key !== toSectionKey) {
            return section;
          }

          const alreadyPresent = section.profiles.some(
            (profile) => getProfileIdentifier(profile) === profileKey,
          );

          if (alreadyPresent) {
            return {
              ...section,
              profiles: section.profiles.map((profile) =>
                getProfileIdentifier(profile) === profileKey
                  ? {
                      ...profile,
                      paymentStatus: nextStatus,
                    }
                  : profile,
              ),
            };
          }

          return adjustSectionCount(
            {
              ...section,
              profiles: [movedProfile as ProfileCard, ...section.profiles],
            },
            1,
          );
        });
      });
    },
    [adjustSectionCount, setProfileSections],
  );

  const handlePaymentStatusChange = useCallback(
    async (
      profile: ProfileCard,
      nextStatus: PaymentStatus,
      fromSectionKey: string,
    ) => {
      const recordId = profile.recordId;

      if (!recordId) {
        toast.error("Unable to determine profile ID.");
        return;
      }

      if (paymentUpdating) {
        return;
      }

      setPaymentUpdating({
        id: recordId,
        status: nextStatus,
      });

      try {
        const response = await putRequest(`/bureau/profile/${recordId}`, {
          paymentStatus: nextStatus,
          followUpStatus: "none",
        });

        if (!response?.success) {
          toast.error(response?.message || "Unable to update payment status.");
          return;
        }

        const profileKey = getProfileIdentifier(profile);
        const toSectionKey = PAYMENT_STATUS_SECTION_MAP[nextStatus];

        if (fromSectionKey !== toSectionKey) {
          moveProfileBetweenSections(
            profileKey,
            fromSectionKey,
            toSectionKey,
            nextStatus,
          );
        }

        toast.success("Payment status updated.");

        await fetchDashboardData();
      } catch (error) {
        console.error("Payment status update failed:", error);

        toast.error("Unable to update payment status.");
      } finally {
        setPaymentUpdating(null);
      }
    },
    [fetchDashboardData, moveProfileBetweenSections, paymentUpdating],
  );

  const renderPaymentActions = useCallback(
    (sectionKey: string, profile: ProfileCard): React.ReactNode => {
      const isFreeSection = sectionKey === "free-profiles";

      const isRedzoneSection = sectionKey === "redzone-profiles";

      if (!isFreeSection && !isRedzoneSection) {
        return null;
      }

      const isUpdating = paymentUpdating?.id === profile.recordId;

      const buttonClass = (tone: string) =>
        [
          "rounded-full",
          "px-2",
          "py-1",
          "text-[10px]",
          "font-semibold",
          "transition",
          tone,
          isUpdating ? "cursor-not-allowed opacity-50" : "hover:opacity-90",
        ].join(" ");

      return (
        <div className="flex flex-wrap gap-1.5">
          {isFreeSection ? (
            <>
              <button
                type="button"
                disabled={isUpdating}
                onClick={() =>
                  handlePaymentStatusChange(profile, "paid", sectionKey)
                }
                className={buttonClass("bg-emerald-100 text-emerald-700")}
              >
                {isUpdating && paymentUpdating?.status === "paid"
                  ? "Updating..."
                  : "Paid"}
              </button>

              <button
                type="button"
                disabled={isUpdating}
                onClick={() =>
                  handlePaymentStatusChange(profile, "redzone", sectionKey)
                }
                className={buttonClass("bg-rose-100 text-rose-700")}
              >
                {isUpdating && paymentUpdating?.status === "redzone"
                  ? "Updating..."
                  : "Red Zone"}
              </button>
            </>
          ) : null}

          {isRedzoneSection ? (
            <>
              <button
                type="button"
                disabled={isUpdating}
                onClick={() =>
                  handlePaymentStatusChange(profile, "paid", sectionKey)
                }
                className={buttonClass("bg-emerald-100 text-emerald-700")}
              >
                {isUpdating && paymentUpdating?.status === "paid"
                  ? "Updating..."
                  : "Paid"}
              </button>

              <button
                type="button"
                disabled={isUpdating}
                onClick={() =>
                  handlePaymentStatusChange(profile, "free", sectionKey)
                }
                className={buttonClass("bg-blue-100 text-blue-700")}
              >
                {isUpdating && paymentUpdating?.status === "free"
                  ? "Updating..."
                  : "Free"}
              </button>
            </>
          ) : null}
        </div>
      );
    },
    [handlePaymentStatusChange, paymentUpdating],
  );

  return {
    paymentUpdating,
    handlePaymentStatusChange,
    moveProfileBetweenSections,
    renderPaymentActions,
  };
};

export default usePaymentStatus;
